/**
 * 🐑🐑🐣 **BREEDING — TWO SHEEP IN A PEN, TURF IN DECEMBER, A LAMB IN MAY.**
 * D130, T284 ②.
 *
 * 🎯 **haugur chose every rule from one table (2026-09-29):**
 *
 * | | His choice | Where it comes from |
 * |---|---|---|
 * | 📅 When | **December only** | rams go in ~1 December; ewes come into heat from ~8 December |
 * | 🐑 Who | **any two of your own sheep, in the same pen** | his answer — no ram and ewe |
 * | 🌱 With | **one `gras` each** — the turf you already dig | his answer |
 * | 💞 How | each fed sheep is ready for **30 s**; two ready ones walk together and stand **2.5 s** | `Minecraft/06` §7 |
 * | 🔁 How often | **once a year** each | a ewe carries one pregnancy a year |
 * | 🤰 How long | **143 game days** → lambs **23 April – 23 May** | Icelandic gestation 142–144 days; sauðburður |
 * | 🐣 How many | **1: 35 % · 2: 60 % · 3: 5 %** | ⚠️ his to change — modern ewes 7/78/14 %, fewer in the 1700s for want of hay |
 * | 🌾 Grown | **on 1 October** after it was born — réttir | real lambs are killed at 4–5 months |
 *
 * 🔴 **EVERYTHING IS IN GAME DAYS — `absoluteDay`, which never resets.** A
 * pregnancy is a calendar thing, so it runs on the world's clock and **stands
 * still while nobody plays** (D41), unlike wool regrowth, which runs on the
 * wall clock. ⚠️ **That is a choice, not an accident**: a lamb due in May must
 * arrive in May, whatever the real month.
 */

import { hungerStage } from './fodder.ts';
import { DAYS_PER_YEAR, WORLD_START_DAY_OF_YEAR, dayOfYearFor } from './clock.ts';
import type { Behaviour, Rng, Sense, Vec3 } from './creature.ts';

/**
 * 🌾🌊🌱🍃 **WHAT A SHEEP WILL EAT TO BE READY, AND HOW GOOD IT IS** — D130,
 * 2026-09-30. 🎯 haugur: hay, seaweed and leaves as well as turf, and
 * *"yes — better feed, more twins."*
 *
 * 📖 **Why the grades** (`Landnam` research, 2026-09-30): **taða**, the home
 * field's hay, was the best feed Iceland had; sheep grazed **the shore** in
 * winter and were fed dried seaweed; **birch** they eat *"only when nothing
 * better is left"* (Vísindavefurinn 4051); and **better-fed ewes had more
 * lambs** — singles were common when the winter hay was poor (the sheep museum;
 * Thoroddsen, c. 1900).
 *
 * 🌾 **`tada` and `uthey` are cut with the ljár** (D131, `hay.ts`): tún gives
 * taða, mýri and gras give úthey. ⚠️ **Úthey's `good` is the Project Manager's
 * grade** — meadow and bog hay was the coarser fodder, so it sits below taða.
 */
export type FeedGrade = 'best' | 'good' | 'poor';

export const FEEDS: ReadonlyMap<string, FeedGrade> = new Map([
  ['tada', 'best'],
  ['uthey', 'good'],
  ['thari', 'good'],
  ['gras', 'poor'],
  ['birkilauf', 'poor'],
]);

/** 🌱 How good this item is as sheep feed, or `undefined` if a sheep will not eat it. */
export function feedGrade(itemKey: string): FeedGrade | undefined {
  return FEEDS.get(itemKey);
}
/** 📅 The month sheep mate in. */
export const MATING_MONTH = 12;
/** 💞 **How long a fed sheep stays ready**, real seconds — Minecraft's love mode. */
export const READY_SECONDS = 30;
/** 🐑🐑 **How long two ready sheep must stand together**, real seconds — Minecraft's. */
export const TOGETHER_SECONDS = 2.5;
/** 📏 **How close is together**, metres, centre to centre — a sheep's length and a bit. */
export const TOGETHER_REACH = 1.3;
/** 🤰 **Gestation, game days** — Icelandic sheep, 142–144. */
export const GESTATION_DAYS = 143;
/**
 * 🔁 **A sheep that mated this many game days ago or less may not again.**
 * ⚠️ Anything longer than December and shorter than a year does it; 300 says
 * *"this season"* without caring which day of December it was.
 */
export const SEASON_DAYS = 300;
/**
 * 🐣 **The odds of 1, 2 and 3 lambs, by how well the ewe was fed.** ⚠️ Each
 * sums to 1, and that is a test. **`best` is his table's own 35/60/5**; the
 * other two are ⚠️ the Project Manager's numbers, his to change.
 */
export const LITTER_BY_GRADE: ReadonlyMap<FeedGrade, readonly (readonly [number, number])[]> =
  new Map([
    [
      'best',
      [
        [1, 0.35],
        [2, 0.6],
        [3, 0.05],
      ],
    ],
    [
      'good',
      [
        [1, 0.5],
        [2, 0.48],
        [3, 0.02],
      ],
    ],
    [
      'poor',
      [
        [1, 0.65],
        [2, 0.35],
        [3, 0],
      ],
    ],
  ]);

/** 🐣 The best feed's odds — the table's 35/60/5. */
export const LITTER_ODDS: readonly (readonly [number, number])[] =
  LITTER_BY_GRADE.get('best') ?? [];
/** 🌾 A lamb is grown on this date, the first time it comes round after its birth. */
export const GROWN_ON = { month: 10, day: 1 } as const;
/** 📏 **A lamb's height as a fraction of an adult's** — at birth, and the day before it is grown. */
export const LAMB_SCALE_AT_BIRTH = 0.55;
export const LAMB_SCALE_GROWN = 0.85;

/**
 * 🧪 **THE TESTING OVERRIDE — parked, like the other two, until haugur says.**
 * `null` is the design. With it on, **any month will do and a lamb comes a
 * day later** — one game day, twenty real minutes — so the thing can be SEEN
 * without three weeks of play. ⚠️ **While it is on, nobody can judge the
 * calendar from this build.** To put it back: set this to `null`.
 */
/** 🧪 The shape of the testing override. */
export type BreedingOverride = {
  readonly anyMonth: boolean;
  readonly gestationDays: number;
} | null;

export const BREEDING_TEST_OVERRIDE: BreedingOverride = {
  anyMonth: true,
  gestationDays: 1,
};

/** 🤰 Gestation as the build is set — the override's, or the real 143. */
export function gestationDays(override: BreedingOverride = BREEDING_TEST_OVERRIDE): number {
  return override?.gestationDays ?? GESTATION_DAYS;
}

/** 📅 Which day of the year an `absoluteDay` falls on, 1 … 365. */
export function dayOfYearOf(absoluteDay: number): number {
  return (
    ((((WORLD_START_DAY_OF_YEAR - 1 + absoluteDay - 1) % DAYS_PER_YEAR) + DAYS_PER_YEAR) %
      DAYS_PER_YEAR) +
    1
  );
}

/** 🌾 **The day a lamb born on `bornDay` is grown** — the next 1 October after it. */
export function grownOn(bornDay: number): number {
  const target = dayOfYearFor(GROWN_ON.month, GROWN_ON.day);
  const today = dayOfYearOf(bornDay);
  const ahead = (target - today + DAYS_PER_YEAR) % DAYS_PER_YEAR;
  /* Born ON 1 October is a whole year from grown, never grown at birth. */
  return bornDay + (ahead === 0 ? DAYS_PER_YEAR : ahead);
}

/** 🐣 Is a sheep born on `bornDay` still a lamb on `today`? `undefined` born means an adult. */
export function isLamb(bornDay: number | undefined, today: number): boolean {
  return bornDay !== undefined && today < grownOn(bornDay);
}

/**
 * 📏 **How big a lamb is, as a fraction of an adult's height** — `1` for a grown
 * sheep. Steady growth from birth to the day it is grown.
 *
 * @param today may carry a fraction of a day, so growth is smooth rather than daily.
 */
export function lambScale(bornDay: number | undefined, today: number): number {
  if (bornDay === undefined) return 1;
  const end = grownOn(bornDay);
  if (today >= end) return 1;
  const t = Math.min(1, Math.max(0, (today - bornDay) / (end - bornDay)));
  return LAMB_SCALE_AT_BIRTH + (LAMB_SCALE_GROWN - LAMB_SCALE_AT_BIRTH) * t;
}

/** What a sheep carries, as the rules need it — its memory, read. */
export interface Breeder {
  /** The game day it last mated, if it ever has. */
  readonly matedDay?: number | undefined;
  /** The game day its lambs are due, while it is carrying. */
  readonly dueDay?: number | undefined;
  /** The game day it was born, for a sheep born in the game. */
  readonly bornDay?: number | undefined;
  /** 🥣 T286 — game days of food left in it. Absent reads as full. */
  readonly reserve?: number | undefined;
}

/** Why a sheep will not take the turf — a message key's last word. */
export type BreedRefusal = 'notDecember' | 'isLamb' | 'carrying' | 'matedThisSeason' | 'hungry';

/**
 * 🌱 **Will this sheep take the turf and be ready?** `null` means yes.
 *
 * ⚠️ **The pen and the owner are the server's to check** — they need the
 * world and the database; this is everything that needs only the sheep and the
 * date.
 */
export function breedRefusal(
  sheep: Breeder,
  today: number,
  month: number,
  override: BreedingOverride = BREEDING_TEST_OVERRIDE,
): BreedRefusal | null {
  if (month !== MATING_MONTH && override?.anyMonth !== true) return 'notDecember';
  if (isLamb(sheep.bornDay, today)) return 'isLamb';
  if (sheep.dueDay !== undefined) return 'carrying';
  if (sheep.matedDay !== undefined && today - sheep.matedDay < seasonDays(override))
    return 'matedThisSeason';
  /* 🥣 T286 — a hungry ewe is not made ready. 📖 RML: ewes should be GAINING
     condition at mating; it is the feed BEFORE that makes twins. */
  if (sheep.reserve !== undefined && hungerStage(sheep.reserve) !== 'fed') return 'hungry';
  return null;
}

/**
 * 🔁 The season as the build is set. ⚠️ **Under the override a sheep may mate
 * again the day after its lambs**, or the thing could be tested once a year.
 */
function seasonDays(override: BreedingOverride): number {
  return override === null ? SEASON_DAYS : gestationDays(override);
}

/**
 * ❤️ **Has it already mated — carrying, or mated this season?** — the sheep
 * that would refuse the turf for that reason, and so wears the heart
 * (protocol 46). ⚠️ The SAME rule as {@link breedRefusal}, asked through it, so
 * the heart can never disagree with what the turf does.
 */
export function hasMated(
  sheep: Breeder,
  today: number,
  override: BreedingOverride = BREEDING_TEST_OVERRIDE,
): boolean {
  /* December is passed as the month so the calendar can never be the reason. */
  const why = breedRefusal(sheep, today, MATING_MONTH, override);
  return why === 'carrying' || why === 'matedThisSeason';
}

/**
 * 🐣 **How many lambs** — one roll against the odds for the feed the ewe had.
 * Rolled when she MATES, and remembered, so a restart before the birth cannot
 * change it.
 */
export function litterSize(rng: Rng, grade: FeedGrade = 'best'): number {
  let roll = rng();
  for (const [n, p] of LITTER_BY_GRADE.get(grade) ?? LITTER_ODDS) {
    if (roll < p) return n;
    roll -= p;
  }
  return 1;
}

/** 🤰 Are the lambs due? */
export function isDue(sheep: Breeder, today: number): boolean {
  return sheep.dueDay !== undefined && today >= sheep.dueDay;
}

/* ══ The mind: three behaviours, and each is a row in `sheep()`'s list. ══ */

function flat(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * 💞 **SeekMate — walk to the other ready sheep.** Above grazing: a ready sheep
 * has somewhere to be. It stops a little short of its mate — two bodies that
 * are solid to each other (T274) cannot share a spot — and {@link TOGETHER_REACH}
 * is measured with that in mind.
 */
export function seekMate(priority: number): Behaviour {
  return {
    name: 'SeekMate',
    priority,
    flags: ['move'],
    wants: (sense: Sense) => sense.mate !== undefined && sense.mate !== null,
    act(sense: Sense) {
      const mate = sense.mate;
      if (mate === undefined || mate === null) return {};
      const d = flat(sense.self.at, mate);
      /* 🧍 Arrived: stand together. `hold`, or the herd picks a new spot. */
      if (d <= TOGETHER_REACH * 0.8) return { goal: null, lookAt: mate, hold: true };
      /* 🔴 AT the mate, not at a spot short of it. ⚠️ Aiming 0.9 m short left
         both sheep 1.34 m apart for ever: each arrived within the body's own
         half-metre of its goal, just outside TOGETHER_REACH, and a new goal
         that close was already "arrived". Found by the server test that walks
         two real sheep together. The stop above is what keeps them apart. */
      const goal = { x: mate.x, y: sense.self.at.y, z: mate.z };
      return { goal, pace: 1.2 };
    },
  };
}

/** 🐣 **A lamb keeps within this of its mother, metres** — his table. */
export const MOTHER_NEAR = 4;
/** 🏃 **Past this, it RUNS to catch up**, metres. */
export const MOTHER_FAR = 8;

/**
 * 🐣 **FollowMother — a lamb that has strayed goes back to her.** Only past
 * {@link MOTHER_NEAR}: inside it, it grazes, wanders and plays like any sheep.
 * Past {@link MOTHER_FAR} it runs — 🎯 *"if it gets left behind it runs to catch
 * up."*
 */
export function followMother(priority: number): Behaviour {
  return {
    name: 'FollowMother',
    priority,
    flags: ['move'],
    wants: (sense: Sense) =>
      sense.mother !== undefined &&
      sense.mother !== null &&
      flat(sense.self.at, sense.mother) > MOTHER_NEAR,
    act(sense: Sense) {
      const mother = sense.mother;
      if (mother === undefined || mother === null) return {};
      const d = flat(sense.self.at, mother);
      /* Stop at arm's length from her, on the line it came along. */
      const k = d > 0 ? 1.5 / d : 0;
      const goal = {
        x: mother.x + (sense.self.at.x - mother.x) * k,
        y: sense.self.at.y,
        z: mother.z + (sense.self.at.z - mother.z) * k,
      };
      return { goal, pace: d > MOTHER_FAR ? 2.6 : 1.3 };
    },
  };
}

/** 🐑 **How often a lamb near its mother bursts into play**, seconds between, on average. */
export const FROLIC_EVERY = 14;
/** How long one burst lasts, seconds. */
export const FROLIC_SECONDS = 1.6;

/**
 * 🐑💨 **Frolic — a short dash, skipping.** 🎯 *"skips and plays."* Only near
 * its mother. **The skip itself is drawn by the client** from this name on the
 * wire (`moving: 'Frolic'`), the way `Dead` tips a body over: the body hops,
 * the server just runs.
 */
export function frolic(priority: number): Behaviour {
  return {
    name: 'Frolic',
    priority,
    flags: ['move'],
    wants: (sense: Sense) => {
      const mother = sense.mother;
      if (mother === undefined || mother === null) return false;
      const m = sense.self.memory;
      if (m.frolicUntil !== undefined && sense.now < m.frolicUntil) return true;
      if (m.frolicAt === undefined) {
        m.frolicAt = sense.now + FROLIC_EVERY * (0.5 + sense.self.rng());
        return false;
      }
      return sense.now >= m.frolicAt && flat(sense.self.at, mother) <= MOTHER_NEAR;
    },
    act(sense: Sense) {
      const self = sense.self;
      const m = self.memory;
      if (m.frolicUntil === undefined || sense.now >= m.frolicUntil) {
        /* A new burst: somewhere three metres off, at a run. */
        m.frolicUntil = sense.now + FROLIC_SECONDS;
        m.frolicAt = sense.now + FROLIC_SECONDS + FROLIC_EVERY * (0.5 + self.rng());
        const angle = self.rng() * Math.PI * 2;
        return {
          goal: {
            x: self.at.x + Math.cos(angle) * 3,
            y: self.at.y,
            z: self.at.z + Math.sin(angle) * 3,
          },
          pace: 2.2,
        };
      }
      return { goal: self.goal, pace: 2.2 };
    },
  };
}
